import React, { useMemo, useRef, useEffect, useState, useId } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { formatInventoryMeasure, getInventoryProductImage, getInventoryStatusMeta, getInventoryFormatLabel } from '../../utils/inventoryFormatting';
import { getQuickPriceRowStyle } from '../quickPriceList/quickPriceGroupPresentation';
import './inventory.css';

export const inventoryRowKey = row => String(row.inventoryKey || row.varietyId);
export const isWarehouseRow = row => row.trackingMode === 'pack_warehouse';

// Older API versions did not return productId. Keep unlike categories separate
// while allowing a rolling backend/frontend deployment.
export const groupInventoryRows = rows => {
  const products = new Map();
  rows.forEach(row => {
    const key = String(row.productId || `${row.categoryId}:${row.productName}`);
    if (!products.has(key)) products.set(key, { key, name: row.productName || 'Producto', row, varieties: new Map(), rows: [] });
    const product = products.get(key);
    const varietyKey = String(row.varietyId);
    if (!product.varieties.has(varietyKey)) product.varieties.set(varietyKey, { key: varietyKey, name: row.varietyName || 'Variedad', rows: [] });
    product.varieties.get(varietyKey).rows.push(row);
    product.rows.push(row);
  });
  return [...products.values()];
};

function Selection({ label, rows, selected, onSelect, disabled }) {
  const ref = useRef(null);
  const keys = rows.filter(isWarehouseRow).map(inventoryRowKey);
  const count = keys.filter(key => selected.has(key)).length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = count > 0 && count < keys.length; }, [count, keys.length]);
  if (!keys.length || !onSelect) return <span className="inventory-check" />;
  return <label className="inventory-check"><input ref={ref} type="checkbox" aria-label={label}
    checked={count === keys.length} disabled={disabled} onChange={event => onSelect(keys, event.target.checked)} /></label>;
}

function GroupToggle({ level, name, parentName, image, open, controls, onToggle }) {
  const subject = parentName ? `${name} de ${parentName}` : name;
  const childrenLabel = level === 'product' ? 'variedades' : 'formatos';
  return <button type="button" className="inventory-group-toggle" aria-expanded={open} aria-controls={controls}
    aria-label={`${open ? 'Ocultar' : 'Ver'} ${childrenLabel} de ${subject}`} onClick={onToggle}>
    <img src={image} alt="" onError={event => { event.currentTarget.onerror = null; event.currentTarget.style.visibility = 'hidden'; }} />
    <span className="min-w-0 flex-1">
      <span className="inventory-level">{level === 'product' ? 'Producto' : `Variedad · ${parentName}`}</span>
      <strong title={name}>{name}</strong>
    </span>
    {open ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
  </button>;
}

export default function InventoryCatalog({ rows, selected = new Set(), onSelect, onWithdraw, onCount, onMovements, disabled = false }) {
  const products = useMemo(() => groupInventoryRows(rows), [rows]);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedVarieties, setExpandedVarieties] = useState(new Set());
  const catalogId = useId();
  const hasSelection = entries => Boolean(onSelect) && entries.some(row => isWarehouseRow(row) && selected.has(inventoryRowKey(row)));

  return <div className="inventory-catalog" aria-label="Productos, variedades y formatos del inventario">
    <div className="inventory-catalog-heading"><span>Producto / variedad / formato</span><span>En bodega</span><span>Acciones</span></div>
    {products.map(product => {
      const open = expandedProduct === product.key;
      const productSelected = hasSelection(product.rows);
      const productContentId = `${catalogId}-product-${encodeURIComponent(product.key)}`;
      return <section key={product.key} aria-label={product.name}>
        <div className="inventory-catalog-row inventory-product" data-selected={productSelected || undefined} style={getQuickPriceRowStyle('product', productSelected)}>
          <Selection label={`Seleccionar formatos visibles de ${product.name}`} rows={product.rows} selected={selected} onSelect={onSelect} disabled={disabled} />
          <GroupToggle level="product" name={product.name} image={product.row.productImage || getInventoryProductImage(product.row)}
            open={open} controls={productContentId} onToggle={() => {
              setExpandedProduct(open ? null : product.key);
              setExpandedVarieties(new Set());
            }} />
        </div>
        <div id={productContentId} hidden={!open}>
          {open && [...product.varieties.values()].map(variety => {
            const varietyOpen = expandedVarieties.has(variety.key);
            const varietySelected = hasSelection(variety.rows);
            const varietyContentId = `${productContentId}-variety-${variety.key}`;
            return <div key={variety.key}>
              <div className="inventory-catalog-row inventory-variety" data-selected={varietySelected || undefined} style={getQuickPriceRowStyle('variety', varietySelected)}>
                <Selection label={`Seleccionar formatos visibles de ${variety.name} de ${product.name}`} rows={variety.rows} selected={selected} onSelect={onSelect} disabled={disabled} />
                <GroupToggle level="variety" name={variety.name} parentName={product.name}
                  image={variety.rows[0].varietyImage || product.row.productImage || getInventoryProductImage(variety.rows[0])}
                  open={varietyOpen} controls={varietyContentId} onToggle={() => setExpandedVarieties(previous => {
                    const next = new Set(previous);
                    next.has(variety.key) ? next.delete(variety.key) : next.add(variety.key);
                    return next;
                  })} />
              </div>
              <div id={varietyContentId} hidden={!varietyOpen}>
                {varietyOpen && variety.rows.map(row => {
                  const key = inventoryRowKey(row);
                  const pack = isWarehouseRow(row);
                  const status = getInventoryStatusMeta(row);
                  const formatLabel = getInventoryFormatLabel(row);
                  const label = [product.name, variety.name, pack ? formatLabel : null].filter(Boolean).join(' · ');
                  const formatSelected = hasSelection([row]);
                  return <div key={key} className="inventory-catalog-row inventory-format" data-selected={formatSelected || undefined} style={getQuickPriceRowStyle('format', formatSelected)}>
                    <Selection label={`Seleccionar ${label}`} rows={[row]} selected={selected} onSelect={onSelect} disabled={disabled} />
                    <div className="inventory-format-name">
                      <span className="inventory-level">Formato</span>
                      <strong>{formatLabel}</strong>
                      <span className="inventory-format-context" title={`${product.name} / ${variety.name}`}>{product.name} / {variety.name}</span>
                      <small>{pack ? 'Bodega por packs' : 'Inventario actual'}</small>
                    </div>
                    <div className="inventory-balance"><strong className={status.label === 'Negativo' ? 'text-red-700' : ''}>{formatInventoryMeasure(row, row.availableQuantity ?? row.availableKg)}</strong>{status.label !== 'OK' && <span className={`rounded px-2 py-0.5 text-xs ${status.className}`}>{status.label}</span>}</div>
                    <div className="inventory-row-actions">
                      {pack && onWithdraw && <button type="button" disabled={disabled} aria-label={`Retirar ${label}`} onClick={() => onWithdraw([row])}>Retirar</button>}
                      {!pack && onCount && <button type="button" disabled={disabled} aria-label={`Contar ${label}`} onClick={() => onCount([row])}>Contar</button>}
                      <button type="button" aria-label={`Movimientos de ${label}`} onClick={() => onMovements(row)}>Movimientos</button>
                    </div>
                  </div>;
                })}
              </div>
            </div>;
          })}
        </div>
      </section>;
    })}
  </div>;
}
